import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import { DataTable } from '../../../../../ui';
import { uploadPsychometricCSV, getPsychometricResults } from '../../../../../utils/api_service/JobOpenings';
import { getPsychometricColumns, PsychometricResult } from './Functions/GetColumns';

interface ResultsProps {
  jobOpeningId: string;
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

const useSortedResults = (results: PsychometricResult[]) => {
  const [sortConfig, setSortConfig] = useState<SortConfig[]>([]);

  const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortConfig((prev) => {
      const existing = prev.findIndex((s) => s.key === key);
      if (existing !== -1) {
        const updated = [...prev];
        updated[existing] = { key, direction };
        return updated;
      }
      return [{ key, direction }];
    });
  }, []);

  const sortedResults = useCallback(() => {
    if (!sortConfig.length) return results;

    return [...results].sort((a, b) => {
      for (const { key, direction } of sortConfig) {
        const aVal = a[key as keyof PsychometricResult];
        const bVal = b[key as keyof PsychometricResult];

        let comparison = 0;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          comparison = aVal - bVal;
        } else if (typeof aVal === 'string' && typeof bVal === 'string') {
          comparison = aVal.localeCompare(bVal);
        }

        if (comparison !== 0) {
          return direction === 'asc' ? comparison : -comparison;
        }
      }
      return 0;
    });
  }, [results, sortConfig]);

  return { sortConfig, handleSortChange, sortedResults };
};

// ---------- Minimal dependency-free XLSX writer (stored/uncompressed ZIP) ----------

const crc32Table = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[n] = c;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = crc32Table[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function strToBytes(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

function dosDateTime(date: Date) {
  const time =
    ((date.getHours() & 0x1f) << 11) |
    ((date.getMinutes() & 0x3f) << 5) |
    ((Math.floor(date.getSeconds() / 2)) & 0x1f);
  const dosDate =
    (((date.getFullYear() - 1980) & 0x7f) << 9) |
    (((date.getMonth() + 1) & 0x0f) << 5) |
    (date.getDate() & 0x1f);
  return { time, dosDate };
}

interface ZipEntry {
  name: string;
  data: Uint8Array;
}

function buildZip(files: { name: string; content: string }[]): Blob {
  const now = new Date();
  const { time, dosDate } = dosDateTime(now);
  const entries: ZipEntry[] = files.map((f) => ({ name: f.name, data: strToBytes(f.content) }));

  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = strToBytes(entry.name);
    const crc = crc32(entry.data);
    const size = entry.data.length;

    const localHeader = new DataView(new ArrayBuffer(30));
    localHeader.setUint32(0, 0x04034b50, true);
    localHeader.setUint16(4, 20, true);
    localHeader.setUint16(6, 0, true);
    localHeader.setUint16(8, 0, true);
    localHeader.setUint16(10, time, true);
    localHeader.setUint16(12, dosDate, true);
    localHeader.setUint32(14, crc, true);
    localHeader.setUint32(18, size, true);
    localHeader.setUint32(22, size, true);
    localHeader.setUint16(26, nameBytes.length, true);
    localHeader.setUint16(28, 0, true);

    const localChunk = new Uint8Array(30 + nameBytes.length + entry.data.length);
    localChunk.set(new Uint8Array(localHeader.buffer), 0);
    localChunk.set(nameBytes, 30);
    localChunk.set(entry.data, 30 + nameBytes.length);
    localParts.push(localChunk);

    const centralHeader = new DataView(new ArrayBuffer(46));
    centralHeader.setUint32(0, 0x02014b50, true);
    centralHeader.setUint16(4, 20, true);
    centralHeader.setUint16(6, 20, true);
    centralHeader.setUint16(8, 0, true);
    centralHeader.setUint16(10, 0, true);
    centralHeader.setUint16(12, time, true);
    centralHeader.setUint16(14, dosDate, true);
    centralHeader.setUint32(16, crc, true);
    centralHeader.setUint32(20, size, true);
    centralHeader.setUint32(24, size, true);
    centralHeader.setUint16(28, nameBytes.length, true);
    centralHeader.setUint16(30, 0, true);
    centralHeader.setUint16(32, 0, true);
    centralHeader.setUint16(34, 0, true);
    centralHeader.setUint16(36, 0, true);
    centralHeader.setUint32(38, 0, true);
    centralHeader.setUint32(42, offset, true);

    const centralChunk = new Uint8Array(46 + nameBytes.length);
    centralChunk.set(new Uint8Array(centralHeader.buffer), 0);
    centralChunk.set(nameBytes, 46);
    centralParts.push(centralChunk);

    offset += localChunk.length;
  }

  const centralDirSize = centralParts.reduce((sum, p) => sum + p.length, 0);
  const centralDirOffset = offset;

  const endRecord = new DataView(new ArrayBuffer(22));
  endRecord.setUint32(0, 0x06054b50, true);
  endRecord.setUint16(4, 0, true);
  endRecord.setUint16(6, 0, true);
  endRecord.setUint16(8, entries.length, true);
  endRecord.setUint16(10, entries.length, true);
  endRecord.setUint32(12, centralDirSize, true);
  endRecord.setUint32(16, centralDirOffset, true);
  endRecord.setUint16(20, 0, true);

  const allParts = [...localParts, ...centralParts, new Uint8Array(endRecord.buffer)];
  return new Blob(allParts as unknown as BlobPart[], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function colName(index: number): string {
  let name = '';
  let n = index + 1;
  while (n > 0) {
    const rem = (n - 1) % 26;
    name = String.fromCharCode(65 + rem) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

function buildSheetXml(rows: (string | number)[][]): string {
  const rowXml = rows.map((row, rIdx) => {
    const cells = row.map((cell, cIdx) => {
      const ref = `${colName(cIdx)}${rIdx + 1}`;
      if (typeof cell === 'number' && !isNaN(cell)) {
        return `<c r="${ref}"><v>${cell}</v></c>`;
      }
      const text = escapeXml(String(cell ?? ''));
      return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${text}</t></is></c>`;
    }).join('');
    return `<row r="${rIdx + 1}">${cells}</row>`;
  }).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetData>${rowXml}</sheetData>
</worksheet>`;
}

function createXlsxBlob(rows: (string | number)[][]): Blob {
  const sheetXml = buildSheetXml(rows);

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`;

  const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="Results" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;

  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
</Relationships>`;

  return buildZip([
    { name: '[Content_Types].xml', content: contentTypes },
    { name: '_rels/.rels', content: rootRels },
    { name: 'xl/workbook.xml', content: workbookXml },
    { name: 'xl/_rels/workbook.xml.rels', content: workbookRels },
    { name: 'xl/worksheets/sheet1.xml', content: sheetXml },
  ]);
}

const toNumber = (value: any): number | null => {
  if (value === undefined || value === null) return null;
  const num = typeof value === 'number' ? value : Number(value);
  return isNaN(num) ? null : num;
};

// ---------- Component ----------

export const Results: React.FC<ResultsProps> = ({ jobOpeningId }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [results, setResults] = useState<PsychometricResult[]>([]);
  const [psychometricType, setPsychometricType] = useState<'Staff' | 'Managers'>('Staff');
  const [currentPage, setCurrentPage] = useState(1);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [anchorElExport, setAnchorElExport] = useState<null | HTMLElement>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const rowsPerPage = 30;

  const { sortConfig, handleSortChange, sortedResults } = useSortedResults(results);

  useEffect(() => {
    getResults();
  }, []);

  const handleOpenExport = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElExport(event.currentTarget);
  };

  const handleCloseExport = () => {
    setAnchorElExport(null);
  };

  const getResults = async () => {
    setIsLoading(true);
    try {
      const response = await getPsychometricResults(jobOpeningId, currentPage, 30);
      if (response.ok) {
        const data = await response.json();
        setResults(data.results ?? []);
        if (data.psychometricType) {
          setPsychometricType(data.psychometricType);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      setUploadStatus({ type: 'error', message: 'Please upload a valid CSV or XLSX file.' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setPendingFile(file);
    setUploadStatus(null);
    setShowTypeModal(true);
  };

  const handleConfirmUpload = async (selectedType: 'Staff' | 'Managers') => {
    if (!pendingFile) return;

    setShowTypeModal(false);
    setIsUploading(true);

    try {
      const response = await uploadPsychometricCSV(jobOpeningId, selectedType, pendingFile);
      if (!response.ok) throw new Error('Upload failed. Please try again.');
      const data = await response.json();
      setUploadStatus({
        type: 'success',
        message: `Successfully imported ${data.imported} record(s) as ${data.psychometric_type}.`,
      });
      setPsychometricType(data.psychometric_type ?? selectedType);
      setSelectedRows(new Set());
      await getResults();
    } catch (err: any) {
      setUploadStatus({ type: 'error', message: err.message || 'Something went wrong.' });
    } finally {
      setIsUploading(false);
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCancelUpload = () => {
    setShowTypeModal(false);
    setPendingFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ---------- Selection ----------

  const getRowKey = (row: PsychometricResult) =>
    row.participantId ?? row.email ?? JSON.stringify(row);

  const handleSelectRow = (_rowIndex: number, row: PsychometricResult) => {
    const key = getRowKey(row);
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.size === paginatedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(paginatedData.map(getRowKey)));
    }
  };

  const isRowSelected = (_rowIndex: number, row: PsychometricResult) =>
    selectedRows.has(getRowKey(row));

  // ---------- Export ----------

  const getExportRows = () => {
    const selected = sortedResults().filter((r) => selectedRows.has(getRowKey(r)));

    const eastTaskHeaders = Array.from({ length: 20 }, (_, i) => `T${String(i + 1).padStart(2, '0')}`);

    const headers = [
      'Name',
      'Participant ID',
      'Email',
      'Gender',
      'Birth Date',
      'Test Date',
      'Age',
      'Age Category',
      'GCA',
      ...(psychometricType === 'Managers' ? ['GF', 'GC', 'GV', 'GS', 'GSM'] : []),
      'DS',
      'Grit',
      'Growth Mindset',
      'Interpersonal',
      'EQ',
      ...eastTaskHeaders,
      'EAST Rata-rata',
      'EAST Jumlah',
      'EAST Salah',
      'EAST Simpangan',
      'EAST Tinggi',
      'EAST Puncak',
      ...(psychometricType === 'Managers' ? ['INCRITS Percentile'] : []),
    ];

    const rows = selected.map((r) => {
      const fullName = [r.applicant?.firstName, r.applicant?.lastName].filter(Boolean).join(' ');
      const base: (string | number)[] = [
        fullName || '-',
        r.participantId ?? '-',
        r.applicant?.email ?? r.email ?? '-',
        r.gender ?? '-',
        r.birthDate ?? '-',
        r.testDate ?? '-',
        toNumber(r.age) ?? '-',
        r.ageCategory ?? '-',
        toNumber(r.gca) ?? '-',
      ];

      if (psychometricType === 'Managers') {
        base.push(
          toNumber(r.gf) ?? '-',
          toNumber(r.gc) ?? '-',
          toNumber(r.gv) ?? '-',
          toNumber(r.gs) ?? '-',
          toNumber(r.gsm) ?? '-',
        );
      }

      base.push(
        toNumber(r.ds) ?? '-',
        toNumber(r.grit) ?? '-',
        toNumber(r.growthMindset) ?? '-',
        toNumber(r.interpersonal) ?? '-',
        toNumber(r.eq) ?? '-',
      );

      // EAST task columns T01–T20
      for (let i = 0; i < 20; i++) {
        base.push(toNumber(r.eastTasks?.[i]) ?? '-');
      }

      base.push(
        toNumber(r.eastRataRata) ?? '-',
        toNumber(r.eastJumlah) ?? '-',
        toNumber(r.eastSalah) ?? '-',
        toNumber(r.eastSimpangan) ?? '-',
        toNumber(r.eastTinggi) ?? '-',
        toNumber(r.eastPuncak) ?? '-',
      );

      if (psychometricType === 'Managers') {
        base.push(toNumber(r.incritsPercentile) ?? '-');
      }

      return base;
    });

    return { headers, rows };
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const { headers, rows } = getExportRows();
    if (!rows.length) return;

    const today = new Date().toISOString().split('T')[0];
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `psychometric_results_${today}.csv`);
    handleCloseExport();
  };

  const handleExportXLSX = () => {
    const { headers, rows } = getExportRows();
    if (!rows.length) return;

    const today = new Date().toISOString().split('T')[0];
    const blob = createXlsxBlob([headers, ...rows]);
    downloadBlob(blob, `psychometric_results_${today}.xlsx`);
    handleCloseExport();
  };

  const columns = getPsychometricColumns(psychometricType);
  const sorted = sortedResults();
  const totalPages = Math.ceil(sorted.length / rowsPerPage);
  const paginatedData = sorted.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const isAllSelected = paginatedData.length > 0 && selectedRows.size === paginatedData.length;
  const isIndeterminate = selectedRows.size > 0 && selectedRows.size < paginatedData.length;

  const onSortChange = (key: string, direction: 'asc' | 'desc') => {
    handleSortChange(key, direction);
    setCurrentPage(1);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <input
        ref={fileInputRef}
        type='file'
        accept='.csv,.xlsx'
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {results.length > 0 ? `${results.length} result(s) found · ${psychometricType}` : ''}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant='contained'
            onClick={handleOpenExport}
            disabled={selectedRows.size === 0}
            sx={{ height: 42, borderRadius: 2, textTransform: 'none' }}
          >
            Export &#x25BE;
          </Button>
          <Menu
            anchorEl={anchorElExport}
            open={Boolean(anchorElExport)}
            onClose={handleCloseExport}
          >
            <MenuItem onClick={handleExportCSV}>Export as CSV</MenuItem>
            <MenuItem onClick={handleExportXLSX}>Export as XLSX</MenuItem>
          </Menu>

          <Button
            variant='contained'
            onClick={handleImportClick}
            disabled={isUploading}
            sx={{ borderRadius: 2, textTransform: 'none', height: 42 }}
          >
            {isUploading ? 'Uploading...' : 'Import CSV / XLSX'}
          </Button>
        </Box>
      </Box>

      {uploadStatus && (
        <Alert
          severity={uploadStatus.type === 'success' ? 'success' : 'error'}
          sx={{ borderRadius: 2 }}
        >
          {uploadStatus.message}
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={paginatedData}
        isLoading={isLoading}
        rowsPerPage={rowsPerPage}
        currentPage={currentPage}
        totalRecords={sorted.length}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        sortConfig={sortConfig}
        onSortChange={onSortChange}
        hasCheckBox
        onClickRow={(row) => handleSelectRow(-1, row)}
        onSelectAll={handleSelectAll}
        onSelectRow={handleSelectRow}
        isRowSelected={isRowSelected}
        isAllSelected={isAllSelected}
        isIndeterminate={isIndeterminate}
      />

      {showTypeModal && (
        <Dialog
          open={showTypeModal}
          onClose={handleCancelUpload}
          maxWidth='xs'
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>
            Select Psychometric Type
          </DialogTitle>
          <DialogContent>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
              Choose which type this file belongs to before importing
              {pendingFile ? ` "${pendingFile.name}"` : ''}.
            </Typography>

            <Stack spacing={1.5}>
              <Button
                variant='contained'
                fullWidth
                onClick={() => handleConfirmUpload('Staff')}
                sx={{ borderRadius: 2, height: 42, textTransform: 'none' }}
              >
                Staff
              </Button>
              <Button
                variant='contained'
                fullWidth
                onClick={() => handleConfirmUpload('Managers')}
                sx={{ borderRadius: 2, height: 42, textTransform: 'none' }}
              >
                Managers
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelUpload} color='inherit' sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};